import { useData } from '@/hooks/use-data';
import { useUserLocations } from '@/hooks/use-user-locations';
import { useEffect } from 'react';

/**
 * Hook for managing selected location with auto-selection logic
 * Automatically selects the first location if none is selected
 */
export const useSelectedLocation = () => {
  const {
    locations,
    loading: locationsLoading,
    fetched: locationsFetched,
  } = useUserLocations();
  const {
    selectedLocationId,
    selectedLocationName,
    setSelectedLocation,
    clearSelectedLocation,
  } = useData();

  // Auto-select first location if no location is selected and locations are available
  useEffect(() => {
    if (
      locationsFetched &&
      !locationsLoading &&
      locations &&
      locations.length > 0 &&
      !selectedLocationId
    ) {
      const firstLocation = locations[0];
      setSelectedLocation(firstLocation.locationId, firstLocation.locationName);
    }
  }, [
    locationsFetched,
    locationsLoading,
    locations,
    selectedLocationId,
    setSelectedLocation,
  ]);

  // Get the currently selected location object
  const selectedLocation =
    locations?.find((loc) => loc.locationId === selectedLocationId) || null;

  // Handle location selection
  const handleLocationSelect = (locationId: string) => {
    const location = locations?.find((loc) => loc.locationId === locationId);
    if (location) {
      setSelectedLocation(location.locationId, location.locationName);
    }
  };

  return {
    // State
    selectedLocation,
    selectedLocationId,
    selectedLocationName,
    locations,
    locationsLoading,
    locationsFetched,

    // Actions
    setSelectedLocation,
    clearSelectedLocation,
    handleLocationSelect,

    // Computed
    hasSelectedLocation: !!selectedLocationId,
    hasLocations: !!locations && locations.length > 0,
  };
};
